import {
  DayPicker,
  type Matcher,
} from "@daypicker/react";
import {Calendar as CalendarIcon} from "lucide-react";
import {useEffect, useState} from "react";
import {useMatterLocale} from "@/components/contexts/MatterJurisdictionContext";
import {Button} from "@/components/ui/button";
import {Chevron, dayPickerBaseClassNames, monthStart, TopControls} from "@/components/ui/DatePickerRoot";
import {useDialogContainer} from "@/components/ui/dialog-container-context";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {cn} from "@/lib/utils";
import {formatRegionalDate} from "@/utils/date-utils";

export type SingleDateCalendarProps = {
  value?: Date;
  onChange?: (date: Date | undefined) => void;
  disabledDates?: Matcher | Matcher[];
  showTopControls?: boolean;
};

/** The bare single-date calendar body (no trigger), reused by `DatePicker` and
 *  any caller that supplies its own trigger (e.g. an inline-editable table cell). */
export const SingleDateCalendar = ({
  value,
  onChange,
  disabledDates,
  showTopControls = false,
}: SingleDateCalendarProps) => {
  const resolvedInitialMonthTime = monthStart(value ?? new Date()).getTime();
  const [month, setMonth] = useState(() => new Date(resolvedInitialMonthTime));

  useEffect(() => {
    setMonth(new Date(resolvedInitialMonthTime));
  }, [resolvedInitialMonthTime]);

  return (
    <div className="rounded-md border border-gray-200 bg-white shadow-sm">
      <TopControls enabled={showTopControls} onChange={setMonth} month={month}/>
      <DayPicker
        mode="single"
        month={month}
        onMonthChange={setMonth}
        selected={value}
        showOutsideDays
        fixedWeeks
        onSelect={onChange}
        disabled={disabledDates}
        className="p-3"
        classNames={{
          ...dayPickerBaseClassNames,
          day: "relative h-8 w-8 flex-none p-0 text-center text-sm focus-within:relative focus-within:z-20",
          day_button:
            "inline-flex h-8 w-8 items-center justify-center rounded-md p-0 font-normal text-gray-900 aria-selected:opacity-100",
          selected:
            "[&>button]:bg-primary [&>button]:text-primary-foreground [&>button]:hover:bg-primary [&>button]:hover:text-primary-foreground [&>button]:focus:bg-primary [&>button]:focus:text-primary-foreground",
          outside: "day-outside [&>button]:text-muted-foreground",
        }}
        components={{Chevron}}
      />
    </div>
  );
};

export type DatePickerProps = {
  initialValue?: Date;
  onChange?: (date: Date | undefined) => void;
  disabledDates?: Matcher | Matcher[];
  showTopControls?: boolean;
  triggerClassName?: string;
};

export const DatePicker = ({
                             initialValue,
                             onChange,
                             disabledDates,
                             showTopControls = false,
                             triggerClassName,
                           }: DatePickerProps) => {
  const locale = useMatterLocale();
  const [pickerOpen, setPickerOpen] = useState(false);
  const dialogContainer = useDialogContainer();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(initialValue);
  const displayValue = selectedDate ? formatRegionalDate(selectedDate, locale) : <span>Pick a date</span>;

  useEffect(() => {
    setSelectedDate(initialValue);
  }, [initialValue]);

  return (
    <Popover open={pickerOpen} onOpenChange={setPickerOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          className={cn(
            "w-[170px] justify-start pl-3 text-left font-normal",
            !selectedDate && "text-muted-foreground",
            triggerClassName,
          )}
        >
          <span className="min-w-0 flex-1 truncate">{displayValue}</span>
          <CalendarIcon className="h-4 w-4 shrink-0 opacity-50"/>
        </Button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        align="start"
        container={dialogContainer ?? undefined}
      >
        <SingleDateCalendar
          value={selectedDate}
          disabledDates={disabledDates}
          showTopControls={showTopControls}
          onChange={(date) => {
            setSelectedDate(date);
            onChange?.(date);
            setPickerOpen(false);
          }}
        />
      </PopoverContent>
    </Popover>
  );
};
